"use client"

import { useCallback, useEffect, useState, memo } from "react"
import { Button } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Checkbox } from "@/components/ui/checkbox"
import { 
  EqualIcon,
  BanIcon,
  ChevronLeftIcon, 
  ChevronRightIcon,
  ArrowLeftRightIcon,
  SearchIcon,
  XIcon
} from "lucide-react"
import { ColumnType } from "@/types/table"

// Filter types
export type FilterOperator = '=' | '!=' | '<' | '>' | '<=' | '>=' | 'contains' | 'between'

export interface Filter {
  column: string
  operator: FilterOperator
  value: string | number | readonly string[] | null | undefined
  value2?: string | number | readonly string[] | null | undefined // For 'between' operator
}

export interface SelectOption {
  value: string
  label: string
}

interface FilterInputProps {
  column: string
  columnType: ColumnType
  filter: Filter | undefined
  onFilterChange: (column: string, filter: Filter | undefined) => void
  placeholder?: string
  dictionary?: any
  selectOptions?: SelectOption[]
  isSelectInput?: boolean
}

const FilterInput = memo(({ 
  column, 
  columnType, 
  filter, 
  onFilterChange, 
  placeholder, 
  dictionary,
  selectOptions = [],
  isSelectInput = false
}: FilterInputProps) => {
  const [operator, setOperator] = useState<FilterOperator>(filter?.operator || (columnType === 'string' ? 'contains' : '='))
  const [value, setValue] = useState<string>(filter?.value?.toString() || '')
  const [value2, setValue2] = useState<string>(filter?.value2?.toString() || '')
  const [open, setOpen] = useState(false)
  const [selectedValues, setSelectedValues] = useState<string[]>([])

  // Reset state when filter is cleared
  useEffect(() => {
    if (!filter) {
      setValue('')
      setValue2('')
      setSelectedValues([])
      setOperator(columnType === 'string' ? 'contains' : '=')
    }
  }, [filter, columnType])

  useEffect(() => {
    if (isSelectInput && filter?.value) {
      setSelectedValues(filter.value.toString().split(',').filter(Boolean))
    }
  }, [isSelectInput, filter])

  const handleMultiSelect = (selectedValue: string) => {
    let newSelectedValues: string[]
    if (selectedValues.includes(selectedValue)) {
      newSelectedValues = selectedValues.filter(v => v !== selectedValue)
    } else {
      newSelectedValues = [...selectedValues, selectedValue]
    }
    setSelectedValues(newSelectedValues)
    
    if (newSelectedValues.length > 0) {
      onFilterChange(column, {
        column,
        operator: '=',
        value: newSelectedValues.join(',')
      })
    } else {
      onFilterChange(column, undefined)
    }
  }

  const handleSelectAll = () => {
    if (selectedValues.length === selectOptions.length) {
      setSelectedValues([])
      onFilterChange(column, undefined)
    } else {
      const allValues = selectOptions.map(opt => opt.value)
      setSelectedValues(allValues)
      onFilterChange(column, {
        column,
        operator: '=',
        value: allValues.join(',')
      })
    }
  }

  const clearSelection = () => {
    setSelectedValues([])
    onFilterChange(column, undefined)
  }

  const getSummaryText = () => {
    if (selectedValues.length === 0) return dictionary?.filters?.select ?? "Select..."
    if (selectedValues.length === 1) {
      return selectOptions.find(opt => opt.value === selectedValues[0])?.label
    }
    return `${selectedValues.length} ${dictionary?.filters?.selected ?? "selected"}`
  }

  const handleValueChange = useCallback((newValue: string, isSecondValue = false) => {
    if (isSecondValue) {
      setValue2(newValue)
      onFilterChange(column, {
        column,
        operator,
        value: value || null,
        value2: newValue || null
      })
    } else {
      setValue(newValue)
      if (newValue || operator === 'between') {
        onFilterChange(column, {
          column,
          operator,
          value: newValue || null,
          value2: operator === 'between' ? value2 || null : undefined
        })
      } else {
        onFilterChange(column, undefined)
      }
    }
  }, [column, operator, value, value2, onFilterChange])

  // Add effect to update filter when operator changes
  useEffect(() => {
    if (value || (operator === 'between' && value2)) {
      onFilterChange(column, {
        column,
        operator,
        value: value || null,
        value2: operator === 'between' ? value2 || null : undefined
      })
    }
  }, [operator, column, value, value2, onFilterChange])

  const renderOperatorSelect = () => {
    if (columnType === 'string') {
      return null
    }

    const operators: FilterOperator[] = columnType === 'date' || columnType === 'number' || columnType === 'percentage' || columnType === 'currency'
      ? ['=', '!=', '<', '>', '<=', '>=', 'between']
      : ['contains']

    const getOperatorLabel = (op: FilterOperator) => {
      switch (op) {
        case '=':
          return dictionary?.filters?.equals ?? "Equals"
        case '!=':
          return dictionary?.filters?.doesNotEqual ?? "Does not equal"
        case '<':
          return dictionary?.filters?.lessThan ?? "Less than"
        case '>':
          return dictionary?.filters?.greaterThan ?? "Greater than"
        case '<=':
          return dictionary?.filters?.lessThanOrEqualTo ?? "Less than or equal to"
        case '>=':
          return dictionary?.filters?.greaterThanOrEqualTo ?? "Greater than or equal to"
        case 'between':
          return dictionary?.filters?.between ?? "Between"
        default:
          return ''
      }
    }

    return (
      <div className="relative">
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 w-8 p-0 hover:bg-accent hover:text-accent-foreground data-[state=open]:bg-white"
            >
              {operator === '=' ? <EqualIcon className="h-4 w-4" /> : 
               operator === '!=' ? <BanIcon className="h-4 w-4" /> : 
               operator === '<' ? <ChevronLeftIcon className="h-4 w-4" /> : 
               operator === '>' ? <ChevronRightIcon className="h-4 w-4" /> : 
               operator === '<=' ? <div className="flex items-center h-4 w-4 justify-center">
                 <span className="text-[16px] tracking-tighter" style={{ transform: 'scaleX(0.9)' }}>≤</span>
               </div> : 
               operator === '>=' ? <div className="flex items-center h-4 w-4 justify-center">
                 <span className="text-[16px] tracking-tighter" style={{ transform: 'scaleX(0.9)' }}>≥</span>
               </div> : 
               operator === 'between' ? <ArrowLeftRightIcon className="h-4 w-4" /> : 
               <SearchIcon className="h-4 w-4" />}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            className="min-w-[160px] z-50"
            sideOffset={5}
          >
            {operators.map(op => (
              <DropdownMenuItem
                key={op}
                onClick={() => {
                  setOperator(op)
                  onFilterChange(column, { column, operator: op, value, value2 })
                }}
                className="flex items-center gap-2"
              >
                {op === '=' ? <EqualIcon className="h-4 w-4" /> : 
                 op === '!=' ? <BanIcon className="h-4 w-4" /> : 
                 op === '<' ? <ChevronLeftIcon className="h-4 w-4" /> : 
                 op === '>' ? <ChevronRightIcon className="h-4 w-4" /> : 
                 op === '<=' ? <div className="flex items-center h-4 w-4 justify-center">
                   <span className="text-[16px] tracking-tighter" style={{ transform: 'scaleX(0.9)' }}>≤</span>
                 </div> : 
                 op === '>=' ? <div className="flex items-center h-4 w-4 justify-center">
                   <span className="text-[16px] tracking-tighter" style={{ transform: 'scaleX(0.9)' }}>≥</span>
                 </div> : 
                 op === 'between' ? <ArrowLeftRightIcon className="h-4 w-4" /> : 
                 <SearchIcon className="h-4 w-4" />}
                <span>{getOperatorLabel(op)}</span>
              </DropdownMenuItem>
            ))}
            <DropdownMenuItem
              onClick={() => {
                setOperator('=')
                setValue('')
                setValue2('')
                onFilterChange(column, undefined)
              }}
              className="flex items-center gap-2 border-t"
            >
              <XIcon className="h-4 w-4" />
              <span>{dictionary?.filters?.reset ?? "Reset"}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    )
  }

  const renderInput = () => {
    if (isSelectInput) {
      return (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <div className="flex items-center w-full">
              <div className={`h-8 flex-1 text-sm rounded-none flex items-center cursor-pointer ${selectedValues.length > 0 ? 'bg-[#dddddd]' : 'bg-white'}`}>
                <span className="px-2">{getSummaryText()}</span>
              </div>
              {selectedValues.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 rounded-none hover:bg-muted bg-[#dddddd]"
                  onClick={(e) => {
                    e.stopPropagation()
                    clearSelection()
                  }}
                >
                  <XIcon className="h-4 w-4" />
                </Button>
              )}
            </div>
          </PopoverTrigger>
          <PopoverContent 
            className="w-[200px] p-0" 
            align="start"
            side="bottom"
            sideOffset={5}
          >
            <div className="p-1">
              <div 
                className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-accent hover:text-accent-foreground"
                onClick={handleSelectAll}
              >
                <Checkbox 
                  checked={selectedValues.length === selectOptions.length}
                  className="h-4 w-4"
                />
                <span className="text-sm">{dictionary?.filters?.selectAll ?? "Select All"}</span>
              </div>
              <div className="my-1 border-t" />
              {selectOptions.map((option) => (
                <div
                  key={option.value}
                  className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-accent hover:text-accent-foreground"
                  onClick={() => handleMultiSelect(option.value)}
                >
                  <Checkbox 
                    checked={selectedValues.includes(option.value)}
                    className="h-4 w-4"
                  />
                  <span className="text-sm">{option.label}</span>
                </div>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )
    }

    return (
      <div className="w-full">
        <input
          type={columnType === 'date' ? 'date' : 'text'}
          pattern={columnType === 'number' ? '[0-9]*\\.?[0-9]*' : undefined}
          className={`h-8 w-full bg-white text-sm rounded-none px-2 ${value ? 'text-[#ff0000]' : ''}`}
          value={value}
          onChange={(e) => {
            if (columnType === 'number') {
              if (e.target.value === '' || /^[0-9]*\.?[0-9]*$/.test(e.target.value)) {
                handleValueChange(e.target.value)
              }
            } else {
              handleValueChange(e.target.value)
            }
          }}
          placeholder={placeholder}
        />
      </div>
    )
  }

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (open && !((event.target as HTMLElement).closest('.relative'))) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open])

  return (
    <div className="w-full">
      {isSelectInput ? renderInput() : (
        <div className="flex items-center w-full">
          <div className="flex-none">
            {renderOperatorSelect()}
          </div>
          <div className="flex-1">
            {renderInput()}
            {operator === 'between' && !isSelectInput && (
              <div className="w-full">
                <input
                  type={columnType === 'date' ? 'date' : 'text'}
                  pattern={columnType === 'number' ? '[0-9]*\\.?[0-9]*' : undefined}
                  className={`h-8 w-full bg-white text-sm rounded-none px-2 ${value2 ? 'text-[#ff0000]' : ''}`}
                  value={value2}
                  onChange={(e) => {
                    if (columnType === 'number') {
                      if (e.target.value === '' || /^[0-9]*\.?[0-9]*$/.test(e.target.value)) {
                        handleValueChange(e.target.value, true)
                      }
                    } else {
                      handleValueChange(e.target.value, true)
                    }
                  }}
                  placeholder={placeholder}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
})

FilterInput.displayName = 'FilterInput'

export default FilterInput